import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import {
  interviewsQuery,
  isReadable,
  type InterviewOut,
  type InterviewRead,
} from '@/api/interviews';
import { Eyebrow } from '@/components/ui/Eyebrow';
import { StatusDot } from '@/components/ui/StatusDot';
import { cn } from '@/lib/cn';
import { onDateTime } from '@/pages/PersonCard/cardDates';
import { type } from '@/theme/typography';

import { interviewUrl } from './interviewUrls';

interface Props {
  /** The interview being read — excluded from its own list, and the source of the `person_id`. */
  interview: InterviewOut;
  viewerId: number;
}

/**
 * §6.7's **Other interviews** — *"now that this viewer has submitted (or is Recruiter/Admin), the other
 * submitted interviews become reachable — as links, not inlined, so the page stays one person's
 * opinion."*
 *
 * ⚠️ **Links and never inlined, and the reason is the sentence above rather than layout.** Another
 * interviewer's comments rendered underneath this one would put two opinions on one page, which is the
 * anchoring ADR 0006 spends the whole blind gate preventing — reading them is a deliberate act, one click
 * away.
 *
 * ⚠️ **The reader can be blinded while reading their own submitted interview, and this block must not
 * assume otherwise** — found while planning Sprint 11. §6.7's own sentence says *"now that this viewer has
 * submitted"*, and that is true **per candidate**: this reader has submitted for *this* person, so their
 * gate for this person is open. But a `Recruiter` or an `Admin` reading somebody else's submitted
 * interview may still owe their own feedback for the same candidate, and then some rows here arrive as
 * `InterviewMetadataOut` — no verdict, no average, nothing to link to. So the three cases are
 * rendered as three cases: a **readable** row as a link, a **blinded** row as a row with §6.5's sentence
 * and **no link** (the destination would say exactly the same thing), and **drafts not at all**.
 *
 * ⚠️ **Drafts are excluded at the request, not in the component** — `?submitted=true`. The block is *other
 * **submitted** interviews*; a client-side filter would leave a colleague's draft metadata in the response
 * for no reason, and §6.5 names filtering-in-the-component as the edit that loses this protection.
 *
 * **Every row is named** since ADR 0016 (2026-07-30), including a **blinded** one: `interviewer_name`
 * sits beside the `interviewer_id` both shapes always carried, and the argument is that ADR's — a
 * colleague's identity anchors no opinion while their verdict would, and the verdict, the average and
 * every comment are still withheld from a reader who owes their own feedback. This paragraph said *"no
 * name for anybody, because no payload carries one"* until then.
 *
 * **A failed read renders nothing.** This block is *"what was hidden before"*, and an §8 error state over
 * it would put a failure notice under a record that is complete and correct on the page above it.
 */
export function OtherInterviews({ interview, viewerId }: Props) {
  const { t, i18n } = useTranslation('interviews');

  const personId = interview.consideration.person_id;
  const others = useQuery(interviewsQuery(viewerId, { person_id: personId, submitted: true }));

  /*
   * This interview is not in its own list — the page it would link to is the page it is on. Written as a
   * loop rather than a `.filter`, because protection #2's sweep forbids `filter`, `slice`, `sort` and
   * `splice` anywhere under `pages/Interview/` (`fillingIn.test.tsx`): the one list that must never be
   * narrowed is `scores`, and a blunt rule that also catches this is the right trade.
   */
  const rows: InterviewRead[] = [];
  for (const row of others.data?.items ?? []) {
    if (row.id !== interview.id) rows.push(row);
  }

  if (rows.length === 0) return null;

  return (
    <section className="mt-10">
      <Eyebrow count={rows.length}>{t('other.eyebrow')}</Eyebrow>

      <ul className="mt-3 divide-y divide-rule">
        {rows.map((row) => (
          <OtherRow key={row.id} row={row} viewerId={viewerId} language={i18n.language} />
        ))}
      </ul>
    </section>
  );
}

function OtherRow({
  row,
  viewerId,
  language,
}: {
  row: InterviewRead;
  viewerId: number;
  language: string;
}) {
  const { t } = useTranslation('interviews');

  /*
   * ⚠️ **The date says *which* date it is, and it did not until 2026-09-28** (BUG-093). This block
   * renders `submitted_at` — when the feedback landed — while §6.5's card renders `scheduled_at` for
   * the same interview, and both were bare: one interview read *Jul 28, 11:30 AM* on the card and
   * *Jul 30, 11:30 PM* here, on screens a reader meets a minute apart. Neither was wrong; the pair was
   * unreadable, which is what a demo made obvious.
   *
   * **This surface keeps the submission date**, because that is what the block is *about*: every row
   * here is submitted feedback (`?submitted=true`), the reader is looking at what became readable and
   * when, and the conversation's own date is on the card one click away. The two labels are the blinded
   * screen's own words for the two fields, so the product uses one vocabulary for this distinction.
   *
   * **The fallback branch is labelled too, and that is not decoration.** `submitted_at` is nullable on
   * the wire (`InterviewMetadataOut`), so a row that arrives without one falls back to `scheduled_at` —
   * and a fallback rendered under a *Submitted* label would be the one thing worse than an unlabelled
   * date: a date named as something it is not.
   */
  const submittedAt = row.submitted_at;
  const when = (
    <time dateTime={submittedAt ?? row.scheduled_at} className={cn(type.numeral, 'text-sm')}>
      {submittedAt === null
        ? t('other.scheduledOn', { date: onDateTime(row.scheduled_at, language) })
        : t('other.submittedOn', { date: onDateTime(submittedAt, language) })}
    </time>
  );

  const mine = row.interviewer_id === viewerId;

  /*
   * ⚠️ **The blinded row, and it is the load-bearing branch.** Below this line the row is
   * `InterviewMetadataOut`, so a verdict, an average, a comment or a snapshot is a `tsc` error rather
   * than a rendering choice — and there is nothing to link to, because the address would answer with the
   * same shape and the same sentence (TASK-080's blinded state, which names the interviewer here too
   * since BUG-036 — the two surfaces agree about one payload).
   */
  if (!isReadable(row)) {
    return (
      <li className="flex flex-wrap items-center gap-x-4 gap-y-1 py-3">
        {when}
        <span className={cn(type.bodyPrimary, 'font-medium')}>{row.interviewer_name}</span>
        <span className={cn(type.body, 'text-ink-500')}>{t('other.hidden')}</span>
      </li>
    );
  }

  return (
    <li className="flex flex-wrap items-center gap-x-4 gap-y-1 py-3">
      {/* A real `<a>`, so a second opinion can be opened in a new tab beside this one. */}
      {/* Who wrote it, before the date and before the link: on this block the person is the subject. */}
      <span className={cn(type.bodyPrimary, mine && 'font-medium')}>
        {mine ? t('other.you') : row.interviewer_name}
      </span>
      <Link
        to={interviewUrl(row.id)}
        className="rounded text-sm text-accent hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
      >
        {mine ? t('other.yours') : t('other.read')}
      </Link>
      {when}

      {/*
       * The verdict, because it is a field of this payload — and **nothing computed here.** An average
       * per interview exists on no interview shape in the API (only `PersonCardOut.average_score` and
       * §6.5's rows carry one, both computed server-side over the caller's own blind-filtered set), so a
       * mean taken over these `scores` would be a **new derived value invented by the client**, arriving
       * from the direction §7 says this product loses invariants from, and able to disagree with the card
       * two screens away. The comments are one click behind the link, which is §6.7's whole design.
       */}
      {row.overall_recommendation !== null && (
        <span className="ml-auto">
          <StatusDot value={row.overall_recommendation} />
        </span>
      )}
    </li>
  );
}
